/**
 * The summary strip above the leads table: how many leads Core has
 * labelled, how often each labeler agrees with WhatConverts, and, once the
 * team has corrected some leads, how often WhatConverts and each labeler
 * matched the team.
 *
 * Numbers come from GET /whatconverts/lead-labels/stats (Core's copy of the
 * leads for the same window), so they can differ slightly from the live
 * table while the daily import catches up.
 */
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { getLeadLabelStats } from '@/services/whatconvertsService';

import { AI_LABELER, labelerName, percent } from './leadLabels';

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-[150px]">
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums leading-none">{value}</div>
      {note ? <div className="mt-1 text-xs text-muted-foreground">{note}</div> : null}
    </div>
  );
}

export function LeadLabelSummary({ tenantId, days }: { tenantId: string; days: number }) {
  const q = useQuery({
    queryKey: ['wc-lead-label-stats', tenantId, days],
    queryFn: () => getLeadLabelStats(tenantId, days),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">Counting labels…</CardContent>
      </Card>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          The label summary could not be loaded. The labels in the table are still correct.
        </CardContent>
      </Card>
    );
  }

  const s = q.data;
  const ai = s.labelers[AI_LABELER];
  const others = Object.keys(s.labelers).filter((k) => k !== AI_LABELER).sort();

  return (
    <Card data-testid="lead-label-summary">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap gap-6">
          <Figure
            label="Labelled by Core AI"
            value={`${(ai?.labelled ?? 0).toLocaleString()} of ${s.leads.toLocaleString()}`}
            note={`Leads in the last ${s.days} days`}
          />
          <Figure
            label="Agrees with WhatConverts"
            value={percent(ai?.agreement_rate)}
            note={
              ai?.compared
                ? `${ai.agree.toLocaleString()} of ${ai.compared.toLocaleString()} leads both marked`
                : 'No lead marked by both yet'
            }
          />
          {others.map((k) => (
            <Figure
              key={k}
              label={`${labelerName(k)} agrees with WhatConverts`}
              value={percent(s.labelers[k].agreement_rate)}
            />
          ))}
          {s.corrected > 0 ? (
            <>
              <Figure
                label="Checked by the team"
                value={s.corrected.toLocaleString()}
                note="Leads with a team correction"
              />
              <Figure
                label="WhatConverts right"
                value={percent(s.whatconverts_vs_team.accuracy)}
                note={`${s.whatconverts_vs_team.right} of ${s.whatconverts_vs_team.corrected} checked`}
              />
              {[AI_LABELER, ...others]
                .filter((k) => s.labelers[k]?.vs_team.corrected)
                .map((k) => (
                  <Figure
                    key={`team-${k}`}
                    label={`${labelerName(k)} right`}
                    value={percent(s.labelers[k].vs_team.accuracy)}
                    note={`${s.labelers[k].vs_team.right} of ${s.labelers[k].vs_team.corrected} checked`}
                  />
                ))}
            </>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Core's labels and the team's corrections are kept in Core only. Nothing is sent to
          WhatConverts or Google Ads.
          {s.corrected === 0 ? ' Correct a few leads below to see who is right more often.' : ''}
        </p>
      </CardContent>
    </Card>
  );
}
